import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Loader, Lock, X } from 'lucide-react';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { staffManagerApiService } from '../../../services/staffManagerApi';
import type { StaffNote, StaffNoteStatus, StaffNoteType } from '../../../types/staffManager';
import { fromDateInputValue, toDateInputValue } from '../../../utils/staffTenure';
import MarkdownEditor from '../../MarkdownEditor';
import { NOTE_TYPE_META } from './StaffNoteCard';
import { cn } from '../../../utils/cn';

export interface StaffNoteSubjectRef {
  discord_id?: string | null;
  uuid?: string | null;
  user_id?: string | null;
  display_name?: string | null;
}

interface StaffNoteModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  note?: StaffNote | null;
  /** Required in create mode. */
  subject?: StaffNoteSubjectRef | null;
  onClose: () => void;
  onSaved: (note: StaffNote) => void;
}

const TYPES: StaffNoteType[] = ['note', 'warning', 'commendation'];
const STATUSES: StaffNoteStatus[] = ['active', 'revoked', 'expired'];

/** Create or edit a Staff Manager Note: type, markdown body, optional expiry, status (edit only). */
export const StaffNoteModal = ({ isOpen, mode, note = null, subject = null, onClose, onSaved }: StaffNoteModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [type, setType] = useState<StaffNoteType>('note');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<StaffNoteStatus>('active');
  const [expiry, setExpiry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useOnClickOutside(modalRef, onClose);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && note) {
      setType(note.type);
      setContent(note.content);
      setStatus(note.status);
      setExpiry(toDateInputValue(note.expires_at));
    } else {
      setType('note');
      setContent('');
      setStatus('active');
      setExpiry('');
    }
    setError(null);
    setSubmitting(false);
  }, [isOpen, mode, note]);

  const expiresAt = useMemo(() => (expiry ? fromDateInputValue(expiry) : null), [expiry]);
  const expiryInvalid = !!expiry && (expiresAt === null || expiresAt <= Date.now());
  const subjectLabel = subject?.display_name ?? note?.subject_display_name ?? subject?.discord_id ?? note?.subject_discord_id ?? subject?.uuid ?? note?.subject_uuid ?? 'this staff member';
  const canSubmit = content.trim().length > 0 && !expiryInvalid && !submitting && (mode === 'edit' || !!(subject?.discord_id || subject?.uuid || subject?.user_id));

  if (!isOpen) return null;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      let saved: StaffNote;
      if (mode === 'edit' && note) {
        saved = await staffManagerApiService.updateNote(note.id, {
          content: content.trim(),
          type,
          status,
          expires_at: expiresAt,
        });
      } else {
        saved = await staffManagerApiService.createNote({
          ...(subject?.discord_id ? { subject_discord_id: subject.discord_id } : {}),
          ...(subject?.uuid ? { subject_uuid: subject.uuid } : {}),
          ...(subject?.user_id ? { subject_user_id: subject.user_id } : {}),
          ...(subject?.display_name ? { subject_display_name: subject.display_name } : {}),
          type,
          content: content.trim(),
          ...(expiresAt ? { expires_at: expiresAt } : {}),
        });
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the note');
    } finally {
      setSubmitting(false);
    }
  };

  const contentNode = (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div ref={modalRef} className="glass-card p-4 md:p-6 rounded-lg w-full max-w-2xl max-md:max-w-[95vw] max-h-[92vh] overflow-y-auto relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white touch-manipulation" aria-label="Close">
          <X />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500/15 text-amber-300">
            <Lock className="w-4 h-4" />
          </span>
          <div>
            <h2 className="text-xl font-bold text-white leading-tight">{mode === 'edit' ? 'Edit Staff Manager Note' : 'New Staff Manager Note'}</h2>
            <p className="text-xs text-glass-400">
              About <span className="text-white font-semibold">{subjectLabel}</span> · visible only to holders of argus.staff.notes.read
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="inline-flex rounded-lg border border-glass-200/20 p-0.5" role="group" aria-label="Note type">
            {TYPES.map((t) => {
              const meta = NOTE_TYPE_META[t];
              const Icon = meta.icon;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  aria-pressed={type === t}
                  className={cn(
                    'inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold transition-colors touch-manipulation',
                    type === t ? meta.chip : 'text-glass-400 hover:text-white',
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {meta.label}
                </button>
              );
            })}
          </div>

          <MarkdownEditor value={content} onChange={setContent} height={220} placeholder="What should other staff managers know? Markdown is supported." />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-500 mb-1">Expires (optional)</span>
              <input type="date" className="glass-input w-full" value={expiry} min={toDateInputValue(Date.now() + 86_400_000)} onChange={(e) => setExpiry(e.target.value)} />
              {expiryInvalid && <span className="block text-xs text-red-300 mt-1">Pick a date in the future.</span>}
            </label>
            {mode === 'edit' && (
              <label className="block">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-500 mb-1">Status</span>
                <select className="glass-select w-full" value={status} onChange={(e) => setStatus(e.target.value as StaffNoteStatus)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={submitting}>
              Cancel
            </button>
            <button type="button" onClick={() => void submit()} className="glass-button min-w-[8rem]" disabled={!canSubmit}>
              {submitting ? <Loader className="w-4 h-4 animate-spin" /> : mode === 'edit' ? 'Save changes' : 'Create note'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(contentNode, document.body);
};
